import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { ApiKeyService } from '../api/endpoints';

interface ApiKeysContextType {
  apiKeys: any[];
  loading: boolean;
  loadApiKeys: () => Promise<void>;
  createApiKey: (title?: string) => Promise<any>;
  updateApiKeyTitle: (keyId: string, title: string) => Promise<void>;
  deleteApiKey: (keyId: string) => Promise<void>;
}

const ApiKeysContext = createContext<ApiKeysContextType | undefined>(undefined);

export const useApiKeys = () => {
  const context = useContext(ApiKeysContext);
  if (context === undefined) {
    throw new Error('useApiKeys must be used within an ApiKeysProvider');
  }
  return context;
};

interface ApiKeysProviderProps {
  children: ReactNode;
}

export const ApiKeysProvider: React.FC<ApiKeysProviderProps> = ({ children }) => {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const loadApiKeys = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await ApiKeyService.listKeys(user.id);
      if (result.success && result.data) {
        setApiKeys(result.data);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createApiKey = async (title: string = 'Untitled Key') => {
    if (!user) return null;
    
    setLoading(true);
    try {
      const result = await ApiKeyService.createKey(user.id, title);
      if (result.success && result.data) {
        await loadApiKeys(); // Refresh the list
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to create API key:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateApiKeyTitle = async (keyId: string, title: string) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await ApiKeyService.updateKeyTitle(keyId, user.id, title);
      if (result.success) {
        await loadApiKeys(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to update API key title:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteApiKey = async (keyId: string) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await ApiKeyService.deleteKey(keyId, user.id);
      if (result.success) {
        await loadApiKeys(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load API keys when user changes
  useEffect(() => {
    if (user) {
      loadApiKeys();
    }
  }, [user, loadApiKeys]);

  const value: ApiKeysContextType = {
    apiKeys,
    loading,
    loadApiKeys,
    createApiKey,
    updateApiKeyTitle,
    deleteApiKey,
  };

  return (
    <ApiKeysContext.Provider value={value}>
      {children}
    </ApiKeysContext.Provider>
  );
};
